@if ((loadedOnce$ | async) === false) {
  <div class="spinner m-auto mt-40 h-32 w-32 border-8"></div>
}

@if ((loadedOnce$ | async) === true) {
  <div class="border-b bg-aux-gray-light px-8 py-8">
    <div class="mb-3.5 flex items-center justify-between">
      <h1 class="flex h-11 items-end text-2xl font-bold text-aux-gray-darkest">Audit History</h1>
      <div class="flex space-x-2">
        <div class="flex items-center justify-between space-x-4">
          @if (loading$ | async) {
            <aux-processing-loader [message]="processorMessage" />
          }
          @if ((loading$ | async) === false) {
            <button class="btn--hyperlink" (click)="triggerFetchAuditHistory()">
              <span class="ml-2">Refresh Audit History</span>
            </button>
          }
          <button class="btn--hyperlink mr-1 focus:outline-none" (click)="resetFilter()">
            Reset Filters
          </button>

          <aux-audit-history-export-button
            [gridAPI]="exportGridApi"
            [sitesNames]="siteNames"
            [userFormatter]="userFormatter"
            [vendorsNames]="vendorNames"
            [filters]="gridFiltersFormGroup.getRawValue()"
          />
        </div>
      </div>
    </div>

    <form [formGroup]="gridFiltersFormGroup">
      <div class="mb-6 grid w-full grid-cols-10 content-start">
        <div class="col-span-10 flex w-full justify-start">
          <div class="mb-5 mr-2 flex w-full justify-start">
            <div class="w-full min-w-[340px]">
              <div class="mb-1 h-4"></div>
              <aux-input
                placeholder="Search"
                class="mr-2"
                icon="Search"
                iconPosition="left"
                hotKey="/"
                formControlName="actionSearch"
                (keyup)="updateSearch($event.target)"
              />
            </div>

            <div class="w-full min-w-[340px]">
              <div class="mb-1 text-xs">Action:</div>
              <ng-select
                placeholder="All"
                [clearable]="false"
                [multiple]="true"
                formControlName="selectedAction"
                (add)="onMultiselectChange($event, 'selectedAction')"
              >
                <ng-container *ngVar="actions$ | async as actions">
                  @if (actions && actions.length > 1) {
                    <ng-option [value]="''">All</ng-option>
                  }
                  @for (action of actions; track action) {
                    <ng-option [value]="action.id">
                      <span [title]="action.name">{{ action.name }}</span>
                    </ng-option>
                  }
                </ng-container>
              </ng-select>
            </div>
          </div>
          <div class="mb-5 mr-2 w-full" style="width: 480px">
            <div class="mb-1 text-xs">Category</div>
            <ng-select
              placeholder="All"
              [clearable]="false"
              [multiple]="true"
              formControlName="selectedCategory"
              (add)="onMultiselectChange($event, 'selectedCategory')"
            >
              <ng-container *ngVar="category$ | async as categories">
                @if (categories && categories.length > 1) {
                  <ng-option [value]="''">All</ng-option>
                }
                @for (category of categories; track category) {
                  <ng-option [value]="category.id">
                    <span [title]="category.name">{{ category.name }}</span>
                  </ng-option>
                }
              </ng-container>
            </ng-select>
          </div>
          <div class="mb-5 w-full" style="width: 480px">
            <div class="mb-1 text-xs">User</div>
            <ng-select
              placeholder="All"
              [multiple]="true"
              [clearable]="false"
              formControlName="selectedUser"
              (add)="onMultiselectChange($event, 'selectedUser')"
            >
              <ng-container *ngVar="users$ | async as users">
                @if (users && users.length > 1) {
                  <ng-option [value]="''">All</ng-option>
                }
                @for (user of users; track user) {
                  <ng-option [value]="user.sub">
                    <span [title]="user.given_name">
                      {{ user.given_name + ' ' + user.family_name }}
                    </span>
                  </ng-option>
                }
              </ng-container>
            </ng-select>
          </div>
        </div>
        <div class="col-span-10 flex w-full justify-start">
          <div class="flex items-start justify-start">
            <aux-input
              class="mr-2 w-64"
              label="From"
              placeholder="YYYY-MM-DD"
              [type]="'date'"
              formControlName="fromDate"
            />
            <aux-input
              class="mr-2 w-64"
              label="To"
              placeholder="YYYY-MM-DD"
              [type]="'date'"
              formControlName="toDate"
            />
          </div>
          <div class="mr-2 w-full">
            <div class="mb-1 text-xs">Vendor Contract</div>
            <ng-select
              class="big-dropdown"
              placeholder="All"
              [multiple]="true"
              [clearable]="false"
              formControlName="selectedVendor"
              [items]="vendorOptions$ | async"
              bindValue="id"
              bindLabel="name"
              (add)="onMultiselectChange($event, 'selectedVendor')"
            >
              <ng-template let-item="item" ng-option-tmp>
                <div
                  class="block truncate"
                  #text
                  [auxTooltip]="text.offsetWidth < text.scrollWidth ? item.name : ''"
                  [auxTooltipPositions]="optionTooltipPositions"
                >
                  {{ item.name }}
                </div>
              </ng-template>
              <ng-template let-items="items" let-clear="clear" ng-multi-label-tmp>
                @if (items.length === 1) {
                  <div class="ng-value">
                    @for (item of items | slice: 0 : 1; track item) {
                      <div class="flex">
                        <span class="ng-value-icon left" aria-hidden="true" (click)="clear(item)">
                          ×
                        </span>
                        <span
                          class="ng-value-label max-w-[200px] overflow-hidden text-ellipsis"
                          #text
                          [auxTooltip]="
                            text.offsetWidth < text.scrollWidth ? getOptionName(item) : ''
                          "
                          [auxTooltipPositions]="optionTooltipPositions"
                        >
                          {{ getOptionName(item) }}
                        </span>
                      </div>
                    }
                  </div>
                } @else if (items.length > 1) {
                  <div class="ng-value">
                    <span class="ng-value-label">{{ items.length }} Selected</span>
                  </div>
                }
              </ng-template>
            </ng-select>
          </div>
          <div class="w-full">
            <aux-site-select-dropdown
              formControlName="selectedSite"
              placeholder="All"
              menuPosition="left"
              titleKey="name"
              menuPanelWidth="auto"
            />
          </div>
        </div>
      </div>
    </form>
  </div>
}

<div class="px-8 py-6">
  <aux-pagination-grid
    class="audit-history-table"
    [gridOptions]="gridOptions"
    (gridReady)="onGridReady($event)"
    [dataSource]="datasource"
    [filterForm]="gridFiltersFormGroup"
    [serverSideFilters]="serverSideFilters"
    [filterValues$]="filterValues$"
    [sortModel$]="sortModel$"
    [exportGridOptions]="exportGridOptions"
    (firstDataRendered)="firstDataRendered()"
    [paginationKey]="paginationKey"
    [hidePinnedRow]="true"
    [isAgThemeAlpine]="true"
    [offColumnsToFit]="true"
    [idTable]="'auditHistoryGrid'"
  />
</div>
